import { useState, useRef, useEffect, FormEvent } from "react";
import { Loader2, Send, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Msg {
  role: "user" | "assistant";
  content: string;
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/help-assistant`;
const STORAGE_KEY = "bytajobb_help_chat";
const ANON_LIMIT = 5;

const STARTERS = [
  "Hur fungerar matchningen?",
  "Vilka filformat stöds för CV?",
  "Hur sparar jag favoritfilter?",
  "Är min data säker?",
];

export function HelpChat({ isAuthenticated }: { isAuthenticated: boolean }) {
  const { toast } = useToast();
  const [messages, setMessages] = useState<Msg[]>(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as Msg[]) : [];
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch {
      /* ignore */
    }
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const userMsgCount = messages.filter((m) => m.role === "user").length;
  const limitReached = !isAuthenticated && userMsgCount >= ANON_LIMIT;

  async function send(text: string) {
    if (!text.trim() || loading) return;

    if (!isAuthenticated) {
      toast({
        title: "Inloggning krävs",
        description: "Logga in för att ställa frågor till AI-assistenten.",
        variant: "destructive",
      });
      return;
    }

    if (limitReached) {
      toast({
        title: "Gräns nådd",
        description: "Logga in för att ställa fler frågor till AI-assistenten.",
        variant: "destructive",
      });
      return;
    }

    const userMsg: Msg = { role: "user", content: text.trim() };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput("");
    setLoading(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        toast({ title: "Sessionen har upphört", description: "Logga in igen och försök på nytt.", variant: "destructive" });
        setMessages(messages);
        setLoading(false);
        return;
      }

      const resp = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ messages: next }),
      });

      if (!resp.ok || !resp.body) {
        if (resp.status === 429) {
          toast({ title: "För många frågor", description: "Vänta en stund och försök igen.", variant: "destructive" });
        } else if (resp.status === 402) {
          toast({ title: "AI tillfälligt otillgänglig", description: "Försök igen senare.", variant: "destructive" });
        } else {
          toast({ title: "Något gick fel", description: "Kunde inte nå AI-assistenten.", variant: "destructive" });
        }
        setMessages(messages);
        setLoading(false);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let assistantSoFar = "";
      let done = false;

      const upsert = (chunk: string) => {
        assistantSoFar += chunk;
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant") {
            return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: assistantSoFar } : m));
          }
          return [...prev, { role: "assistant", content: assistantSoFar }];
        });
      };

      while (!done) {
        const { done: d, value } = await reader.read();
        if (d) break;
        buf += decoder.decode(value, { stream: true });

        let nl: number;
        while ((nl = buf.indexOf("\n")) !== -1) {
          let line = buf.slice(0, nl);
          buf = buf.slice(nl + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;
          const json = line.slice(6).trim();
          if (json === "[DONE]") {
            done = true;
            break;
          }
          try {
            const parsed = JSON.parse(json);
            const delta = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (delta) upsert(delta);
          } catch {
            buf = line + "\n" + buf;
            break;
          }
        }
      }
    } catch (e) {
      console.error("Chat error:", e);
      toast({ title: "Fel vid AI-anrop", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    send(input);
  };

  const clear = () => {
    setMessages([]);
    sessionStorage.removeItem(STORAGE_KEY);
  };

  return (
    <div className="flex flex-col h-[60vh] sm:h-[55vh]">
      <div ref={scrollRef} className="flex-1 overflow-y-auto pr-1 space-y-3">
        {messages.length === 0 && (
          <div className="text-center py-6 space-y-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="font-medium">Fråga AI-assistenten</p>
              <p className="text-sm text-muted-foreground mt-1">
                Få svar på dina frågor om Bytajobb.nu
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-md mx-auto">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-left text-sm rounded-md border px-3 py-2 hover:border-primary hover:bg-accent transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-foreground"
              }`}
            >
              {m.content || (loading && i === messages.length - 1 ? "…" : "")}
            </div>
          </div>
        ))}

        {loading && messages[messages.length - 1]?.role === "user" && (
          <div className="flex justify-start">
            <div className="bg-muted rounded-2xl px-3 py-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          </div>
        )}
      </div>

      {limitReached && (
        <p className="text-xs text-destructive text-center py-2">
          Du har använt {ANON_LIMIT} frågor. Logga in för att fortsätta.
        </p>
      )}

      <form onSubmit={onSubmit} className="mt-3 flex gap-2 items-end">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send(input);
            }
          }}
          placeholder={limitReached ? "Logga in för att fortsätta…" : "Skriv din fråga…"}
          disabled={loading || limitReached}
          rows={1}
          className="min-h-[44px] max-h-32 resize-none"
        />
        <Button type="submit" size="icon" disabled={loading || !input.trim() || limitReached}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
        {messages.length > 0 && (
          <Button type="button" variant="ghost" size="icon" onClick={clear} aria-label="Rensa konversation">
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </form>
    </div>
  );
}
